import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { DndContext, DragOverlay, PointerSensor, pointerWithin, useDraggable, useDroppable, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  ArrowDown,
  ArrowLeft,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Copy,
  GripVertical,
  LayoutGrid,
  Loader2,
  Pencil,
  Plus,
  RotateCcw,
  Save,
  Search,
  Settings as SettingsIcon,
  StickyNote,
  Trash2,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/hooks/use-toast'
import { apiService } from '@/services/api'
import { cn } from '@/lib/utils'

const EXERCISE_PLAN_TEMPLATE_TABS = [
  { id: 'create', label: 'Create', icon: Plus },
  { id: 'edit', label: 'Edit', icon: Pencil },
  { id: 'categories', label: 'Categories', icon: SettingsIcon },
]

const EMPTY_PAGINATION = { pageIndex: 0, pageSize: 25 }
const DAY_ORDER = ['su', 'mo', 'tu', 'we', 'th', 'fr', 'sa']
const DAY_LABELS = { su: 'Su', mo: 'Mo', tu: 'Tu', we: 'We', th: 'Th', fr: 'Fr', sa: 'Sa' }
const DAY_FIELD_MAP = { su: 'sunday', mo: 'monday', tu: 'tuesday', we: 'wednesday', th: 'thursday', fr: 'friday', sa: 'saturday' }
const BULK_FIELDS = ['sets', 'reps', 'rest', 'speed']

const EmptyState = ({ message }) => (
  <div className="uploader-empty">
    {message}
  </div>
)

const PageHero = ({ title, description, onBack }) => (
  <div className="app-page-hero">
    <div className="absolute right-0 top-0 h-36 w-36 rounded-full hidden" />
    <div className="app-page-hero-content space-y-4">
      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
      ) : null}
      <div className="space-y-2">
        <h1 className="text-3xl font-semibold">{title}</h1>
        <p className="max-w-3xl text-base leading-7">{description}</p>
      </div>
    </div>
  </div>
)

const WorkspaceTabs = ({ tabs, activeTab, onChange }) => (
  <div className="uploader-tabs">
    {tabs.map((tab) => {
      const Icon = tab.icon
      const isActive = activeTab === tab.id
      return (
        <button
          key={tab.id}
          type="button"
          onClick={() => onChange(tab.id)}
          className={cn(
            '-mb-px inline-flex items-center gap-2 border-b-2 px-1 pb-3 pt-1 text-sm font-semibold transition-colors',
            isActive ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-600 hover:text-slate-900'
          )}
        >
          <Icon className="h-4 w-4" />
          {tab.label}
        </button>
      )
    })}
  </div>
)

const PaginationFooter = ({ pageIndex, pageSize, totalCount, onPageChange }) => {
  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize))
  const from = totalCount ? pageIndex * pageSize + 1 : 0
  const to = Math.min(totalCount, pageIndex * pageSize + pageSize)

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between">
      <p>
        Showing <span className="font-medium text-slate-900">{from}-{to}</span> of{' '}
        <span className="font-medium text-slate-900">{totalCount}</span>
      </p>
      <div className="flex items-center gap-2">
        <Button type="button" variant="ghost" size="icon" onClick={() => onPageChange(Math.max(0, pageIndex - 1))} disabled={pageIndex === 0}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="min-w-[88px] text-center">
          Page {Math.min(pageIndex + 1, pageCount)} of {pageCount}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => onPageChange(Math.min(pageCount - 1, pageIndex + 1))}
          disabled={pageIndex >= pageCount - 1}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}

const createDraftId = (prefix = 'draft') => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

const tryParseJSON = (value) => {
  if (typeof value !== 'string') return value
  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

const firstArray = (...values) => values.find((value) => Array.isArray(value)) || []
const truthyDay = (value) => value === 1 || value === '1' || value === true
const hasMeaningfulValue = (value) => value !== null && value !== undefined && String(value).trim() !== ''
const usesConditioningFields = (exercise = {}) =>
  ['mode', 'intensity', 'heartrate', 'duration'].some((field) => hasMeaningfulValue(exercise?.[field]))

const createBulkDefaults = (seed = {}) => ({
  sets: seed.sets ?? '',
  reps: seed.reps ?? '',
  rest: seed.rest ?? '',
  speed: seed.speed ?? '',
})

const applyDefaultsToRow = (row, defaults = {}) => {
  const next = { ...row }
  BULK_FIELDS.forEach((field) => {
    if (hasMeaningfulValue(defaults?.[field])) next[field] = defaults[field]
  })
  return next
}

const mergeDefaults = (...defaultsList) =>
  defaultsList.reduce(
    (acc, current) => {
      BULK_FIELDS.forEach((field) => {
        if (hasMeaningfulValue(current?.[field])) acc[field] = current[field]
      })
      return acc
    },
    createBulkDefaults()
  )

const createExerciseRowDraft = (seed = {}, defaults = {}) => {
  const resolvedDefaults = mergeDefaults(defaults)
  return {
    id: seed.id || createDraftId('exercise-row'),
    persistedId: seed.persistedId ?? seed.id ?? null,
    templateId: seed.templateId ?? seed.exercisetemplate_id ?? seed.sourceId ?? null,
    catId: seed.catId ?? seed.cat_id ?? seed.catid ?? '',
    catName: seed.catName || seed.cat_name || seed.catname || seed.category || '',
    name: seed.name || 'Exercise',
    sets: seed.sets ?? resolvedDefaults.sets,
    reps: seed.reps ?? resolvedDefaults.reps,
    rest: seed.rest ?? resolvedDefaults.rest,
    speed: seed.speed ?? resolvedDefaults.speed,
    mode: seed.mode ?? '',
    intensity: seed.intensity ?? '',
    heartrate: seed.heartrate ?? '',
    duration: seed.duration ?? '',
    note: seed.note ?? '',
    description: seed.description ?? '',
    video_url: seed.video_url ?? '',
  }
}

const createExercisePlanDraft = (name, defaults = {}) => ({
  id: createDraftId('exercise-plan'),
  persistedId: null,
  name,
  days: [],
  defaults: createBulkDefaults(defaults),
  exercises: [],
})

const buildExercisePlanTemplatePayload = (form) => ({
  name: form.name.trim(),
  description: form.description.trim(),
  instructions: form.instructions.trim(),
  category: form.categoryId ? Number(form.categoryId) : form.newCategoryName.trim(),
  subcat: form.newCategoryParentId ? Number(form.newCategoryParentId) : -1,
  exerciseplantemplateplans: (form.plans || []).map((plan) => ({
    id: plan.persistedId || undefined,
    name: plan.name.trim(),
    ...DAY_ORDER.reduce((acc, day) => {
      acc[DAY_FIELD_MAP[day]] = plan.days.includes(day) ? 1 : 0
      return acc
    }, {}),
    sets: hasMeaningfulValue(plan.defaults?.sets) ? Number(plan.defaults.sets) || plan.defaults.sets : null,
    reps: plan.defaults?.reps ?? '',
    rest: plan.defaults?.rest ?? '',
    speed: plan.defaults?.speed ?? '',
    exerciseplantemplateplanexercises: (plan.exercises || []).map((exercise, index) => ({
      id: exercise.persistedId || undefined,
      exercisetemplate_id: exercise.templateId ? Number(exercise.templateId) : undefined,
      cat_id: exercise.catId ? Number(exercise.catId) : 0,
      cat_name: exercise.catName || '',
      name: exercise.name || '',
      sets: hasMeaningfulValue(exercise.sets) ? Number(exercise.sets) || exercise.sets : null,
      reps: exercise.reps ?? '',
      rest: exercise.rest ?? '',
      speed: exercise.speed ?? '',
      mode: exercise.mode ?? '',
      intensity: hasMeaningfulValue(exercise.intensity) ? exercise.intensity : null,
      heartrate: hasMeaningfulValue(exercise.heartrate) ? exercise.heartrate : null,
      duration: hasMeaningfulValue(exercise.duration) ? exercise.duration : null,
      note: exercise.note || '',
      display_order: index + 1,
    })),
  })),
})

const mapExerciseTreeNode = (node, index, parentKey = 'root', rootSection = '', depth = 0) => {
  const childrenSource = tryParseJSON(
    node?.children || node?.child || node?.nodes || node?.items || node?.subItems || node?.exerciseplancategoryindex || []
  )
  const nodeName = node?.name || node?.title || node?.label || 'Item'
  const nextRootSection = depth === 0 ? nodeName : rootSection
  const rawId = node?.id || node?.guid || node?.value || node?.name || `node-${index}`
  const stableId = `${parentKey}-${String(rawId)}-${index}`
  const children = Array.isArray(childrenSource)
    ? childrenSource.map((child, childIndex) => mapExerciseTreeNode(child, childIndex, stableId, nextRootSection, depth + 1))
    : []

  return {
    id: stableId,
    sourceId: node?.id || node?.guid || node?.value || null,
    templateId: node?.templateId || node?.template_id || node?.exercisetemplate_id || node?.exercise_id || node?.id || null,
    templateSourceId:
      node?.exerciseplantemplate?.id ||
      node?.exerciseplantemplate_id ||
      node?.exercise_plan_template_id ||
      null,
    isExercisePlanTemplate: Boolean(node?.exerciseplantemplate || node?.exerciseplantemplate_id || node?.exercise_plan_template_id),
    name: nodeName,
    category: node?.category || node?.catname || node?.cat_name || node?.parent_name || '',
    catid: node?.catid || node?.cat_id || node?.exercisetemplatecategory_id || node?.category_id || node?.parent_id || null,
    catname: node?.catname || node?.cat_name || node?.category || node?.parent_name || '',
    sets: node?.sets ?? '',
    reps: node?.reps ?? '',
    rest: node?.rest ?? '',
    speed: node?.speed ?? '',
    mode: node?.mode ?? '',
    intensity: node?.intensity ?? '',
    heartrate: node?.heartrate ?? '',
    duration: node?.duration ?? '',
    note: node?.note ?? '',
    description: node?.description || '',
    video_url: node?.video_url || '',
    rootSection: nextRootSection,
    depth,
    children,
  }
}

const normalizeExercisePlanLibraryTree = (raw) => {
  const sourceRoot = Array.isArray(raw) ? raw : raw?.data || raw || []
  const nested = sourceRoot?.[0]?.exerciseplancategoryindex || sourceRoot?.exerciseplancategoryindex || sourceRoot
  const parsed = tryParseJSON(nested)
  return Array.isArray(parsed) ? parsed.map((node, index) => mapExerciseTreeNode(node, index)) : []
}

const countLeaves = (node) => {
  if (!node?.children?.length) return 1
  return node.children.reduce((total, child) => total + countLeaves(child), 0)
}

const buildLibraryExpansionState = (nodes, value) =>
  (nodes || []).reduce((acc, node) => {
    acc[node.id] = value
    if (node.children?.length) Object.assign(acc, buildLibraryExpansionState(node.children, value))
    return acc
  }, {})

const filterLibraryTree = (nodes, term) => {
  const normalizedTerm = String(term || '').trim().toLowerCase()
  if (!normalizedTerm) return nodes

  return (nodes || [])
    .map((node) => {
      const children = filterLibraryTree(node.children || [], normalizedTerm)
      const matchesSelf =
        String(node.name || '').toLowerCase().includes(normalizedTerm) ||
        String(node.category || node.catname || '').toLowerCase().includes(normalizedTerm)

      if (matchesSelf || children.length) return { ...node, children }
      return null
    })
    .filter(Boolean)
}

const flattenExerciseNode = (node) => {
  if (!node?.children?.length) return [node]
  return node.children.flatMap(flattenExerciseNode)
}

const classifyExerciseDropNode = (node) => {
  if (node?.isExercisePlanTemplate) return 'plan-template'
  if (node?.children?.length) return 'exercise-group'
  return 'single-exercise'
}

const normalizeExercisePlanTemplateDetail = (raw, fallbackRow = null) => {
  const data = raw?.data || raw || {}
  const plansRaw = firstArray(
    data?.exerciseplantemplateplans,
    data?.exerciseplans,
    data?.plans,
    fallbackRow?.exerciseplantemplateplans
  )

  return {
    id: data?.id || fallbackRow?.id || null,
    name: data?.name || fallbackRow?.name || '',
    description: data?.description || fallbackRow?.description || '',
    instructions: data?.instructions || fallbackRow?.instructions || '',
    categoryId: `${data?.exerciseplantemplatecategory_id ?? fallbackRow?.exerciseplantemplatecategory_id ?? ''}`,
    newCategoryName: '',
    newCategoryParentId: '',
    globalDefaults: createBulkDefaults(),
    plans: plansRaw.map((plan) => {
      const exercisesRaw = firstArray(plan?.exerciseplantemplateplanexercises, plan?.exerciseplanexercises, plan?.exercises)
      return {
        id: createDraftId('edit-plan'),
        persistedId: plan?.id || null,
        name: plan?.name || 'Workout',
        days: DAY_ORDER.filter((day) => truthyDay(plan?.[DAY_FIELD_MAP[day]])),
        defaults: createBulkDefaults({
          sets: plan?.sets ?? '',
          reps: plan?.reps ?? '',
          rest: plan?.rest ?? '',
          speed: plan?.speed ?? '',
        }),
        exercises: exercisesRaw.map((exercise) =>
          createExerciseRowDraft({
            id: createDraftId('edit-row'),
            persistedId: exercise?.id || null,
            templateId: exercise?.exercisetemplate_id || null,
            catId: exercise?.cat_id || '',
            catName: exercise?.cat_name || '',
            name: exercise?.name || 'Exercise',
            sets: exercise?.sets ?? '',
            reps: exercise?.reps ?? '',
            rest: exercise?.rest ?? '',
            speed: exercise?.speed ?? '',
            mode: exercise?.mode ?? '',
            intensity: exercise?.intensity ?? '',
            heartrate: exercise?.heartrate ?? '',
            duration: exercise?.duration ?? '',
            note: exercise?.note ?? '',
            description: exercise?.description ?? '',
            video_url: exercise?.video_url ?? '',
          })
        ),
      }
    }),
  }
}

const cloneImportedPlan = (plan) => ({
  ...plan,
  id: createDraftId('import-plan'),
  persistedId: null,
  name: plan.name || 'Imported Workout',
  exercises: (plan.exercises || []).map((exercise) => ({
    ...exercise,
    id: createDraftId('import-row'),
    persistedId: null,
  })),
})

const normalizeTextField = (value) => (hasMeaningfulValue(value) ? String(value).trim() : '')
const normalizeOptionalField = (value) => (hasMeaningfulValue(value) ? `${value}`.trim() : '')

const buildTemplateComparable = (form = {}) => ({
  name: normalizeTextField(form.name),
  description: normalizeTextField(form.description),
  instructions: normalizeTextField(form.instructions),
  categoryId: normalizeOptionalField(form.categoryId),
})

const buildPlanComparable = (plan = {}) => ({
  name: normalizeTextField(plan.name),
  days: DAY_ORDER.filter((day) => plan.days?.includes(day)),
  sets: normalizeOptionalField(plan.defaults?.sets),
  reps: normalizeOptionalField(plan.defaults?.reps),
  rest: normalizeOptionalField(plan.defaults?.rest),
  speed: normalizeOptionalField(plan.defaults?.speed),
})

const buildExerciseComparable = (exercise = {}) => ({
  templateId: normalizeOptionalField(exercise.templateId),
  catId: normalizeOptionalField(exercise.catId),
  catName: normalizeTextField(exercise.catName),
  name: normalizeTextField(exercise.name),
  sets: normalizeOptionalField(exercise.sets),
  reps: normalizeOptionalField(exercise.reps),
  rest: normalizeOptionalField(exercise.rest),
  speed: normalizeOptionalField(exercise.speed),
  mode: normalizeOptionalField(exercise.mode),
  intensity: normalizeOptionalField(exercise.intensity),
  heartrate: normalizeOptionalField(exercise.heartrate),
  duration: normalizeOptionalField(exercise.duration),
  note: exercise.note ?? '',
})

const areEqualByJson = (left, right) => JSON.stringify(left) === JSON.stringify(right)

const buildTemplateUpdatePayload = (form = {}) => ({
  name: normalizeTextField(form.name),
  description: normalizeTextField(form.description),
  instructions: normalizeTextField(form.instructions),
  exerciseplantemplatecategory_id: form.categoryId ? Number(form.categoryId) : null,
})

const buildPlanUpdatePayload = (plan = {}) => ({
  name: normalizeTextField(plan.name),
  ...DAY_ORDER.reduce((acc, day) => {
    acc[DAY_FIELD_MAP[day]] = plan.days?.includes(day) ? 1 : 0
    return acc
  }, {}),
  sets: hasMeaningfulValue(plan.defaults?.sets) ? Number(plan.defaults.sets) || plan.defaults.sets : null,
  reps: normalizeOptionalField(plan.defaults?.reps),
  rest: normalizeOptionalField(plan.defaults?.rest),
  speed: normalizeOptionalField(plan.defaults?.speed),
})

const buildExerciseUpdatePayload = (exercise = {}) => ({
  name: normalizeTextField(exercise.name),
  cat_id: exercise.catId ? Number(exercise.catId) : 0,
  cat_name: normalizeTextField(exercise.catName),
  sets: hasMeaningfulValue(exercise.sets) ? Number(exercise.sets) || exercise.sets : null,
  reps: normalizeOptionalField(exercise.reps),
  rest: normalizeOptionalField(exercise.rest),
  speed: normalizeOptionalField(exercise.speed),
  mode: normalizeOptionalField(exercise.mode),
  intensity: hasMeaningfulValue(exercise.intensity) ? exercise.intensity : null,
  heartrate: hasMeaningfulValue(exercise.heartrate) ? exercise.heartrate : null,
  duration: hasMeaningfulValue(exercise.duration) ? exercise.duration : null,
  note: exercise.note || '',
})

const LibraryGroup = ({ node, expanded, onToggle, childrenCount, children }) => (
  <div className="space-y-2 rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
    <button type="button" onClick={onToggle} className="flex w-full items-center justify-between gap-3 text-left">
      <div className="flex items-center gap-2">
        {expanded ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
        <span className="text-sm font-semibold text-slate-800">{node.name}</span>
      </div>
      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500">{childrenCount}</span>
    </button>
    {expanded ? <div className="space-y-2 pl-4">{children}</div> : null}
  </div>
)

const ExerciseLibraryLeaf = ({ node }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useDraggable({
    id: `exercise-${node.id}`,
    data: { type: 'exerciseLibraryItem', node },
  })
  const style = { transform: CSS.Transform.toString(transform), transition }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cn(
        'group flex cursor-grab items-center justify-between gap-3 rounded-xl border px-3 py-2 text-sm shadow-sm transition active:cursor-grabbing',
        isDragging ? 'border-slate-200 bg-white/40 opacity-0' : 'border-slate-200 bg-white hover:border-fitness-primary/40'
      )}
    >
      <div className="min-w-0">
        <p className="break-words text-sm font-semibold text-slate-800">{node.name}</p>
        <div className="mt-1 flex flex-wrap gap-2 text-[11px] font-semibold text-slate-500">
          {node.catname || node.category ? <span className="rounded-full bg-slate-100 px-2 py-0.5">{node.catname || node.category}</span> : null}
          {hasMeaningfulValue(node.sets) ? <span className="rounded-full bg-slate-100 px-2 py-0.5">Sets {node.sets}</span> : null}
          {hasMeaningfulValue(node.reps) ? <span className="rounded-full bg-slate-100 px-2 py-0.5">Reps {node.reps}</span> : null}
        </div>
      </div>
      <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-slate-400 transition group-hover:border-fitness-primary/40 group-hover:text-fitness-primary">
        <GripVertical className="h-4 w-4" />
      </div>
    </div>
  )
}

const ExerciseLibraryDragOverlay = ({ node }) => (
  <div className="pointer-events-none flex items-center justify-between gap-4 rounded-lg border border-fitness-primary/40 bg-white px-4 py-3 text-sm shadow-sm ring-2 ring-fitness-primary/20">
    <div className="min-w-0">
      <p className="break-words text-sm font-semibold text-slate-800">{node?.name}</p>
      <div className="mt-1 flex flex-wrap gap-2 text-[11px] font-semibold text-slate-500">
        {node?.catname || node?.category ? <span className="rounded-full bg-slate-100 px-2 py-0.5">{node?.catname || node?.category}</span> : null}
        {hasMeaningfulValue(node?.sets) ? <span className="rounded-full bg-slate-100 px-2 py-0.5">Sets {node.sets}</span> : null}
        {hasMeaningfulValue(node?.reps) ? <span className="rounded-full bg-slate-100 px-2 py-0.5">Reps {node.reps}</span> : null}
      </div>
    </div>
    <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-fitness-primary/40 bg-fitness-primary/10 text-fitness-primary">
      <GripVertical className="h-4 w-4" />
    </div>
  </div>
)

const ExerciseCanvasDropZone = ({ mode, children }) => {
  const { setNodeRef, isOver } = useDroppable({ id: `exercise-plan-canvas-${mode}`, data: { type: 'planCanvas', mode } })
  return (
    <div ref={setNodeRef} className={cn('rounded-lg transition', isOver && 'ring-2 ring-fitness-primary/40')}>
      {children}
    </div>
  )
}

const ExercisePlanDropZone = ({ planId, children }) => {
  const { setNodeRef, isOver } = useDroppable({ id: `exercise-plan-drop-${planId}`, data: { type: 'planDrop', planId } })
  return (
    <div ref={setNodeRef} className={cn('rounded-lg transition', isOver && 'ring-2 ring-fitness-primary/40')}>
      {children}
    </div>
  )
}

const MetricField = ({ label, value, onChange }) => (
  <div className="grid gap-1">
    <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</span>
    <Input value={value ?? ''} onChange={(event) => onChange(event.target.value)} className="h-9 text-xs" />
  </div>
)

const SortableExerciseTemplateRow = ({
  planId,
  exercise,
  categories,
  readOnlyCategory = false,
  onFieldChange,
  onCopy,
  onDelete,
  onNote,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: exercise.id,
    data: { type: 'exerciseRow', planId, exerciseId: exercise.id },
  })
  const style = { transform: CSS.Transform.toString(transform), transition }
  const hasNote = Boolean(exercise.note && String(exercise.note).trim())
  const showConditioningFields = usesConditioningFields(exercise)
  const metricFields = showConditioningFields
    ? [
        ['Mode', 'mode'],
        ['Intensity', 'intensity'],
        ['Heart Rate', 'heartrate'],
        ['Duration', 'duration'],
      ]
    : [
        ['Sets', 'sets'],
        ['Reps', 'reps'],
        ['Rest', 'rest'],
        ['Speed', 'speed'],
      ]

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'grid grid-cols-[auto,minmax(120px,0.8fr),minmax(220px,1.2fr),minmax(0,2.1fr),auto] items-start gap-3 rounded-xl border border-slate-200 bg-white px-3 py-3',
        isDragging && 'bg-white shadow-sm ring-1 ring-fitness-primary/30'
      )}
    >

      <div className="grid gap-1 min-w-0">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
          {readOnlyCategory ? 'Category' : 'Body Part'}
        </span>
        {readOnlyCategory ? (
          <div className="flex h-9 items-center rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-700">
            {exercise.catName || 'Uncategorized'}
          </div>
        ) : (
          <select
            value={exercise.catId || ''}
            onChange={(event) => {
              const value = event.target.value
              const match = categories.find((category) => `${category.id}` === `${value}`)
              onFieldChange('catId', value)
              onFieldChange('catName', match?.name || '')
            }}
            className="flex h-9 w-full rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-900 focus:border-fitness-primary focus:outline-none"
          >
            <option value="">Select body part</option>
            {categories.map((category) => (
              <option key={`${exercise.id}-cat-${category.id}`} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="grid gap-1 min-w-0">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Name</span>
        <Input value={exercise.name || ''} onChange={(event) => onFieldChange('name', event.target.value)} className="h-9 text-xs" />
      </div>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        {metricFields.map(([label, field]) => (
          <MetricField
            key={`${exercise.id}-${field}`}
            label={label}
            value={exercise[field]}
            onChange={(value) => onFieldChange(field, value)}
          />
        ))}
      </div>

      <div className="mt-6 flex items-center justify-end gap-1">
        <button
          type="button"
          onClick={onCopy}
          className="rounded-md border border-slate-200 p-2 text-slate-500 transition hover:border-slate-300 hover:text-slate-800"
          title="Copy row"
        >
          <Copy className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={onNote}
          className={cn(
            'rounded-md border p-2 transition hover:border-slate-300 hover:text-slate-800',
            hasNote ? 'border-fitness-primary/40 text-fitness-primary' : 'border-slate-200 text-slate-500'
          )}
          title={hasNote ? 'Edit note' : 'Add note'}
        >
          <StickyNote className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="rounded-md border border-red-200 p-2 text-red-500 transition hover:border-red-300 hover:text-red-600"
          title="Delete row"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}

const ExercisePlanCanvas = ({
  mode,
  form,
  newPlanName,
  onNewPlanNameChange,
  onAddPlan,
  globalDefaults,
  onGlobalDefaultChange,
  onApplyGlobalDefaults,
  onPlanNameChange,
  onPlanDefaultsChange,
  onApplyPlanDefaults,
  onPlanCopy,
  onPlanDelete,
  onPlanDayToggle,
  onAddExerciseRow,
  onRowFieldChange,
  onRowCopy,
  onRowDelete,
  onRequestNote,
  libraryTree,
  libraryExpanded,
  onToggleLibraryNode,
  onExpandAllLibrary,
  libraryFilter,
  onLibraryFilterChange,
  activeDragNode,
  onDragStart,
  onDragCancel,
  onDragEnd,
  onReset,
  onSave,
  saveLabel,
  saveLoading,
}) => {
  const isEditMode = mode === 'edit'
  const filteredLibraryTree = useMemo(() => filterLibraryTree(libraryTree, libraryFilter), [libraryTree, libraryFilter])
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }))
  const railBoundaryRef = useRef(null)
  const sideRailAnchorRef = useRef(null)
  const rightRailRef = useRef(null)
  const [desktopRailStyle, setDesktopRailStyle] = useState(null)
  const [bulkDefaultsModalOpen, setBulkDefaultsModalOpen] = useState(false)
  const libraryPortalTarget = typeof document !== 'undefined' ? document.body : null
  const desktopRailPinned = Boolean(desktopRailStyle)

  const collisionDetection = useCallback(
    (args) => {
      const activeType = args.active?.data?.current?.type
      if (activeType === 'exerciseLibraryItem' && args.pointerCoordinates && rightRailRef.current) {
        const rect = rightRailRef.current.getBoundingClientRect()
        const { x, y } = args.pointerCoordinates
        if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
          return []
        }
      }
      return pointerWithin(args)
    },
    []
  )

  useEffect(() => {
    if (typeof window === 'undefined') return undefined

    const updateDesktopRail = () => {
      if (window.innerWidth < 1280) {
        setDesktopRailStyle(null)
        return
      }

      const boundary = railBoundaryRef.current
      const anchor = sideRailAnchorRef.current
      const content = rightRailRef.current
      if (!boundary || !anchor || !content) {
        setDesktopRailStyle(null)
        return
      }

      const boundaryRect = boundary.getBoundingClientRect()
      const anchorRect = anchor.getBoundingClientRect()
      const contentRect = content.getBoundingClientRect()
      const topOffset = 24
      const bottomOffset = 24
      const viewportHeight = window.innerHeight
      const maxRailHeight = Math.max(320, viewportHeight - topOffset - bottomOffset)
      const stopPinningAt = topOffset + Math.min(contentRect.height, maxRailHeight)

      if (anchorRect.top > topOffset || boundaryRect.bottom <= stopPinningAt) {
        setDesktopRailStyle(null)
        return
      }

      setDesktopRailStyle({
        top: topOffset,
        left: anchorRect.left,
        width: anchorRect.width,
        height: Math.min(maxRailHeight, Math.max(320, boundaryRect.bottom - topOffset - bottomOffset)),
        placeholderHeight: contentRect.height,
      })
    }

    updateDesktopRail()
    window.addEventListener('scroll', updateDesktopRail, { passive: true })
    window.addEventListener('resize', updateDesktopRail)
    return () => {
      window.removeEventListener('scroll', updateDesktopRail)
      window.removeEventListener('resize', updateDesktopRail)
    }
  }, [filteredLibraryTree.length, form.plans.length, libraryExpanded])

  const renderRightRail = () => (
    <div ref={rightRailRef} className="flex flex-col gap-4 pb-8 xl:h-full xl:overflow-y-auto xl:pr-1">
      <Card className="uploader-rail-card border-0 shadow-none ring-0">
        <CardHeader>
          <CardTitle className="text-base font-semibold text-slate-900">Exercise Library</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input value={libraryFilter} onChange={(event) => onLibraryFilterChange(event.target.value)} placeholder="Exercise / Plan filter" className="pl-9" />
            </div>
            <label className="flex items-center gap-2 whitespace-nowrap text-xs font-semibold text-slate-600">
              <input
                type="checkbox"
                checked={Object.values(libraryExpanded).every(Boolean) && Object.keys(libraryExpanded).length > 0}
                onChange={(event) => onExpandAllLibrary(event.target.checked)}
                className="h-4 w-4 rounded border-slate-300"
              />
              Expand all
            </label>
          </div>
          <div className="max-h-[34rem] space-y-3 overflow-auto pr-1">
            {filteredLibraryTree.length ? (
              filteredLibraryTree.map((node) => {
                const expanded = libraryExpanded[node.id]
                const renderNode = (child) =>
                  !child.children?.length ? (
                    <ExerciseLibraryLeaf key={child.id} node={child} />
                  ) : (
                    <LibraryGroup
                      key={child.id}
                      node={child}
                      expanded={libraryExpanded[child.id]}
                      onToggle={() => onToggleLibraryNode(child.id)}
                      childrenCount={countLeaves(child)}
                    >
                      {(child.children || []).map((grandChild) => renderNode(grandChild))}
                    </LibraryGroup>
                  )
                return (
                  <LibraryGroup
                    key={node.id}
                    node={node}
                    expanded={expanded}
                    onToggle={() => onToggleLibraryNode(node.id)}
                    childrenCount={countLeaves(node)}
                  >
                    {(node.children || []).map((child) => renderNode(child))}
                  </LibraryGroup>
                )
              })
            ) : (
              <EmptyState message="No library results matched the current filter." />
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )

  return (
    <div className="space-y-6">
      <Card className="uploader-rail-card border-0 shadow-none ring-0">
        <CardContent className="space-y-6 p-6">
          <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
            <div className="space-y-5">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Name</label>
                <Input value={form.name} onChange={(event) => form.onFieldChange('name', event.target.value)} placeholder="Exercise plan template name" />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Exercise Plan Category</label>
                  <select
                    className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-fitness-primary focus:outline-none"
                    value={form.categoryId}
                    onChange={(event) => form.onFieldChange('categoryId', event.target.value)}
                  >
                    <option value="">Select category</option>
                    {form.categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Make Subcategory Of</label>
                  <select
                    className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-fitness-primary focus:outline-none"
                    value={form.newCategoryParentId}
                    onChange={(event) => form.onFieldChange('newCategoryParentId', event.target.value)}
                  >
                    <option value="">No parent category</option>
                    {form.categories
                      .filter((category) => !category.parent_id)
                      .map((category) => (
                        <option key={`parent-${category.id}`} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">New Category</label>
                <Input value={form.newCategoryName} onChange={(event) => form.onFieldChange('newCategoryName', event.target.value)} placeholder="Create a new category if needed" />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Description</label>
                <Textarea value={form.description} onChange={(event) => form.onFieldChange('description', event.target.value)} placeholder="Template description" className="min-h-[140px]" />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Instructions</label>
                <Textarea value={form.instructions} onChange={(event) => form.onFieldChange('instructions', event.target.value)} placeholder="Coaching or execution instructions" className="min-h-[140px]" />
              </div>
            </div>

            <div className="uploader-highlight-panel">
              <div className="space-y-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-fitness-primary">Builder</p>
                  <h3 className="mt-2 text-xl font-semibold text-slate-900">
                    {mode === 'create' ? 'Design a reusable exercise plan' : 'Refine the selected exercise plan'}
                  </h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    Build one or more workout cards, apply defaults at the workout or full-template level, and use the same drag-and-drop language the consultant screen already uses.
                  </p>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-lg border border-slate-200 bg-white p-4">
                    <p className="text-xs uppercase tracking-wide text-slate-500">Workout Plans</p>
                    <p className="mt-1 text-2xl font-semibold text-slate-900">{form.plans.length}</p>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white p-4">
                    <p className="text-xs uppercase tracking-wide text-slate-500">Exercise Rows</p>
                    <p className="mt-1 text-2xl font-semibold text-slate-900">{(form.plans || []).reduce((total, plan) => total + (plan.exercises?.length || 0), 0)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div ref={railBoundaryRef} className="grid gap-6 xl:grid-cols-[minmax(0,2fr),minmax(320px,0.95fr)]">
            <DndContext sensors={sensors} collisionDetection={collisionDetection} onDragStart={onDragStart} onDragCancel={onDragCancel} onDragEnd={onDragEnd} autoScroll={false}>
              <div className="space-y-5">
                <Card className="uploader-rail-card border-0 shadow-none ring-0">
                  <CardContent className="space-y-4 p-5">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex flex-1 flex-wrap items-center gap-3">
                        <Input value={newPlanName} onChange={(event) => onNewPlanNameChange(event.target.value)} placeholder="New plan name" className="max-w-sm" />
                        <Button type="button" onClick={onAddPlan} disabled={!newPlanName.trim()}>
                          <Plus className="mr-2 h-4 w-4" />
                          Add Plan
                        </Button>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Button type="button" variant="outline" onClick={() => setBulkDefaultsModalOpen(true)}>
                          <SettingsIcon className="mr-2 h-4 w-4" />
                          Apply to all
                        </Button>
                        <Button type="button" variant="outline" onClick={onApplyGlobalDefaults}>
                          <ArrowDown className="mr-2 h-4 w-4" />
                          Run Defaults
                        </Button>
                      </div>
                    </div>

                    <ExerciseCanvasDropZone mode={mode}>
                      {form.plans.length ? (
                        <div className="space-y-5">
                          {form.plans.map((plan) => (
                            <ExercisePlanDropZone key={plan.id} planId={plan.id}>
                              <Card className="uploader-rail-card border-0 shadow-none ring-0">
                                <CardHeader className="space-y-4 pb-3">
                                  <div className="flex flex-wrap items-center justify-between gap-3">
                                    <Input value={plan.name} onChange={(event) => onPlanNameChange(plan.id, event.target.value)} className="max-w-md text-sm font-semibold" />
                                    <div className="flex flex-wrap items-end gap-2">
                                      {BULK_FIELDS.map((field) => (
                                        <div key={`${plan.id}-${field}`} className="min-w-[110px]">
                                          <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">{field}</label>
                                          <Input value={plan.defaults?.[field] || ''} onChange={(event) => onPlanDefaultsChange(plan.id, field, event.target.value)} className="h-9 text-xs" />
                                        </div>
                                      ))}
                                      <Button type="button" variant="outline" size="icon" onClick={() => onApplyPlanDefaults(plan.id)} title="Apply to this workout">
                                        <ArrowDown className="h-4 w-4" />
                                      </Button>
                                      <Button type="button" variant="outline" size="icon" onClick={() => onAddExerciseRow(plan.id)} title="Add exercise row">
                                        <Plus className="h-4 w-4" />
                                      </Button>
                                      <Button type="button" variant="outline" size="icon" onClick={() => onPlanCopy(plan.id)} title="Copy workout">
                                        <Copy className="h-4 w-4" />
                                      </Button>
                                      <Button type="button" variant="outline" size="icon" onClick={() => onPlanDelete(plan.id)} className="border-red-200 text-red-500 hover:border-red-300 hover:text-red-600" title="Delete workout">
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </div>

                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Active Days</span>
                                    {DAY_ORDER.map((day) => (
                                      <label
                                        key={`${plan.id}-${day}`}
                                        className={cn(
                                          'flex items-center gap-1 rounded-full border px-2 py-1 text-xs font-semibold transition',
                                          plan.days.includes(day)
                                            ? 'border-fitness-primary/30 bg-fitness-primary/10 text-fitness-primary'
                                            : 'border-slate-200 bg-white text-slate-500'
                                        )}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={plan.days.includes(day)}
                                          onChange={(event) => onPlanDayToggle(plan.id, day, event.target.checked)}
                                          className="h-3 w-3 rounded border-slate-300"
                                        />
                                        {DAY_LABELS[day]}
                                      </label>
                                    ))}
                                  </div>
                                </CardHeader>

                                <CardContent className="space-y-3 pt-0">
                                  <div className="overflow-x-auto">
                                    <div className="min-w-[1080px] space-y-2">
                                      <div className="grid grid-cols-[auto,minmax(120px,0.8fr),minmax(220px,1.2fr),minmax(0,2.1fr),auto] items-center gap-3 rounded-xl bg-slate-50 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500 ring-1 ring-slate-200">
                                        <span />
                                        <span>{isEditMode ? 'Category' : 'Body Part'}</span>
                                        <span>Name</span>
                                        <span>Prescription</span>
                                        <span className="text-right">Actions</span>
                                      </div>

                                      <SortableContext items={(plan.exercises || []).map((exercise) => exercise.id)} strategy={verticalListSortingStrategy}>
                                        {(plan.exercises || []).map((exercise) => (
                                          <SortableExerciseTemplateRow
                                            key={exercise.id}
                                            planId={plan.id}
                                            exercise={exercise}
                                            categories={form.categories}
                                            readOnlyCategory={isEditMode}
                                            onFieldChange={(field, value) => onRowFieldChange(plan.id, exercise.id, field, value)}
                                            onCopy={() => onRowCopy(plan.id, exercise.id)}
                                            onDelete={() => onRowDelete(plan.id, exercise.id)}
                                            onNote={() => onRequestNote(plan.id, exercise)}
                                          />
                                        ))}
                                      </SortableContext>
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                            </ExercisePlanDropZone>
                          ))}
                        </div>
                      ) : (
                        <EmptyState message="Drop an exercise plan template here, or add a plan manually to start building workout cards." />
                      )}
                    </ExerciseCanvasDropZone>
                  </CardContent>
                </Card>

                <div className="flex flex-wrap items-center justify-end gap-3">
                  <Button type="button" variant="outline" onClick={onReset}>
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Reset Changes
                  </Button>
                  <Button type="button" onClick={onSave} disabled={saveLoading}>
                    {saveLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                    {saveLabel}
                  </Button>
                </div>
              </div>

              <div ref={sideRailAnchorRef} className="xl:self-start">
                {desktopRailPinned ? (
                  <div aria-hidden="true" className="hidden xl:block" style={{ height: desktopRailStyle.placeholderHeight }} />
                ) : (
                  <div className="flex flex-col pb-8 xl:h-[calc(100vh-3rem)] xl:overflow-hidden">
                    {renderRightRail()}
                  </div>
                )}
              </div>

              {desktopRailPinned && libraryPortalTarget
                ? createPortal(
                    <div
                      className="hidden overflow-hidden xl:flex xl:flex-col"
                      style={{
                        position: 'fixed',
                        top: desktopRailStyle.top,
                        left: desktopRailStyle.left,
                        width: desktopRailStyle.width,
                        height: desktopRailStyle.height,
                        zIndex: 20,
                      }}
                    >
                      {renderRightRail()}
                    </div>,
                    libraryPortalTarget
                  )
                : null}

              {libraryPortalTarget
                ? createPortal(
                    <DragOverlay dropAnimation={null} style={{ zIndex: 2147483647, pointerEvents: 'none' }}>
                      {activeDragNode ? <ExerciseLibraryDragOverlay node={activeDragNode} /> : null}
                    </DragOverlay>,
                    libraryPortalTarget
                  )
                : (
                    <DragOverlay dropAnimation={null} style={{ zIndex: 2147483647, pointerEvents: 'none' }}>
                      {activeDragNode ? <ExerciseLibraryDragOverlay node={activeDragNode} /> : null}
                    </DragOverlay>
                  )}
            </DndContext>
          </div>
        </CardContent>
      </Card>
      <Dialog open={bulkDefaultsModalOpen} onOpenChange={setBulkDefaultsModalOpen}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>Apply defaults to all workouts</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            {BULK_FIELDS.map((field) => (
              <div key={`bulk-default-${field}`} className="space-y-2">
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{field}</label>
                <Input value={globalDefaults[field] || ''} onChange={(event) => onGlobalDefaultChange(field, event.target.value)} className="h-10" />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setBulkDefaultsModalOpen(false)}>
              Close
            </Button>
            <Button
              type="button"
              onClick={() => {
                onApplyGlobalDefaults()
                setBulkDefaultsModalOpen(false)
              }}
            >
              <ArrowDown className="mr-2 h-4 w-4" />
              Apply to all workouts
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function ExercisePlanTemplatesWorkspace({ companyUrl, activeTab, onTabChange, onBack }) {
  const { toast } = useToast()
  const [categories, setCategories] = useState([])
  const [allCategoryRows, setAllCategoryRows] = useState([])
  const [categoryRows, setCategoryRows] = useState([])
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [loadingCategoryRows, setLoadingCategoryRows] = useState(false)
  const [updatingCategoryId, setUpdatingCategoryId] = useState(null)

  const [libraryTree, setLibraryTree] = useState([])
  const [libraryExpanded, setLibraryExpanded] = useState({})
  const [libraryFilter, setLibraryFilter] = useState('')

  const [createFormState, setCreateFormState] = useState({
    name: '',
    description: '',
    instructions: '',
    categoryId: '',
    newCategoryName: '',
    newCategoryParentId: '',
    globalDefaults: createBulkDefaults(),
    plans: [],
  })
  const [createPlanName, setCreatePlanName] = useState('')
  const [createSaving, setCreateSaving] = useState(false)
  const [createDragNode, setCreateDragNode] = useState(null)

  const [programRows, setProgramRows] = useState([])
  const [programSearch, setProgramSearch] = useState('')
  const [programPagination, setProgramPagination] = useState({ ...EMPTY_PAGINATION })
  const [programTotalCount, setProgramTotalCount] = useState(0)
  const [selectedProgramIds, setSelectedProgramIds] = useState([])
  const [loadingProgramRows, setLoadingProgramRows] = useState(false)
  const [selectedProgramId, setSelectedProgramId] = useState(null)
  const [loadingProgramDetail, setLoadingProgramDetail] = useState(false)
  const [editFormState, setEditFormState] = useState(null)
  const [editBaseline, setEditBaseline] = useState(null)
  const [editPlanName, setEditPlanName] = useState('')
  const [editSaving, setEditSaving] = useState(false)
  const [editDragNode, setEditDragNode] = useState(null)
  const [editCatalogCollapsed, setEditCatalogCollapsed] = useState(false)

  const [noteDialog, setNoteDialog] = useState(null)
  const [noteDraft, setNoteDraft] = useState('')
  const [deleteCategoryDialog, setDeleteCategoryDialog] = useState(null)
  const [deletingCategoryId, setDeletingCategoryId] = useState(null)
  const [deleteExerciseDialog, setDeleteExerciseDialog] = useState(null)
  const [deletingExerciseId, setDeletingExerciseId] = useState(null)

  const topLevelCategories = useMemo(() => allCategoryRows.filter((row) => !row.parent_id), [allCategoryRows])
  const categoryFilterOptions = useMemo(() => {
    const source = categories.length ? categories : allCategoryRows
    return source.filter((row) => row?.id && row?.name).sort((a, b) => String(a.name).localeCompare(String(b.name)))
  }, [allCategoryRows, categories])

  const updateScopedForm = useCallback((scope, updater) => {
    if (scope === 'create') {
      setCreateFormState((current) => updater(current))
      return
    }
    setEditFormState((current) => (current ? updater(current) : current))
  }, [])

  const buildFormBridge = useCallback(
    (state, setState) => ({
      ...state,
      categories,
      onFieldChange: (key, value) => {
        setState((current) => {
          const next = { ...current, [key]: value }
          if (key === 'categoryId' && value !== '') {
            next.newCategoryName = ''
            next.newCategoryParentId = ''
          } else if ((key === 'newCategoryName' || key === 'newCategoryParentId') && `${value}`.trim() !== '') {
            next.categoryId = ''
          }
          return next
        })
      },
    }),
    [categories]
  )

  const createForm = useMemo(() => buildFormBridge(createFormState, setCreateFormState), [buildFormBridge, createFormState])
  const editForm = useMemo(() => (editFormState ? buildFormBridge(editFormState, setEditFormState) : null), [buildFormBridge, editFormState])

  const loadCategories = useCallback(async () => {
    if (!companyUrl) return
    const response = await apiService.fetchExercisePlanTemplateCategories(companyUrl)
    if (!response.success) {
      toast({ title: 'Unable to load exercise plan categories', description: response.error, variant: 'destructive' })
      return
    }
    const rows = Array.isArray(response.data) ? response.data : []
    setCategories(rows)
    setAllCategoryRows(rows)
  }, [companyUrl, toast])

  const loadCategoryRows = useCallback(
    async (selectedCategoryId = 'all') => {
      if (!companyUrl) return
      setLoadingCategoryRows(true)
      const response = await apiService.fetchExercisePlanTemplateCategories(companyUrl, selectedCategoryId === 'all' ? null : selectedCategoryId)
      setLoadingCategoryRows(false)
      if (!response.success) {
        toast({ title: 'Unable to load category layout', description: response.error, variant: 'destructive' })
        return
      }
      const rows = Array.isArray(response.data) ? response.data : []
      setCategoryRows(rows)
      if (selectedCategoryId === 'all') setAllCategoryRows(rows)
    },
    [companyUrl, toast]
  )

  const loadProgramRows = useCallback(async () => {
    if (!companyUrl) return
    setLoadingProgramRows(true)
    const response = await apiService.fetchExercisePlanTemplates(companyUrl, {
      orderByOn: 'name',
      orderBy: 'ASC',
      limit: programPagination.pageSize,
      fromIndex: programPagination.pageIndex * programPagination.pageSize,
      searchtext: programSearch.trim(),
    })
    setLoadingProgramRows(false)
    if (!response.success) {
      toast({ title: 'Unable to load exercise plan templates', description: response.error, variant: 'destructive' })
      return
    }
    setProgramRows(Array.isArray(response.data?.rows) ? response.data.rows : [])
    setProgramTotalCount(Number(response.data?.count) || 0)
    setSelectedProgramIds([])
  }, [companyUrl, programPagination.pageIndex, programPagination.pageSize, programSearch, toast])

  const loadLibraryTree = useCallback(async () => {
    if (!companyUrl) return
    const response = await apiService.fetchExercisePlanTemplateTree(companyUrl)
    if (!response.success) {
      toast({ title: 'Unable to load exercise library', description: response.error, variant: 'destructive' })
      return
    }
    const normalized = normalizeExercisePlanLibraryTree(response.data)
    setLibraryTree(normalized)
    setLibraryExpanded(buildLibraryExpansionState(normalized, false))
  }, [companyUrl, toast])

  const loadProgramDetail = useCallback(
    async (programId, row = null) => {
      if (!companyUrl || !programId) return
      setSelectedProgramId(programId)
      setLoadingProgramDetail(true)
      const response = await apiService.fetchExercisePlanTemplateDetails(companyUrl, programId)
      setLoadingProgramDetail(false)
      if (!response.success) {
        toast({ title: 'Unable to load exercise plan template detail', description: response.error, variant: 'destructive' })
        return
      }
      const normalized = normalizeExercisePlanTemplateDetail(response.data, row)
      setEditFormState(normalized)
      setEditBaseline(normalized)
      setEditPlanName('')
      setEditCatalogCollapsed(true)
    },
    [companyUrl, toast]
  )

  useEffect(() => {
    if (!companyUrl) return
    loadCategories()
    loadCategoryRows('all')
    loadLibraryTree()
  }, [companyUrl, loadCategories, loadCategoryRows, loadLibraryTree])

  useEffect(() => {
    loadProgramRows()
  }, [loadProgramRows])

  const updatePlans = useCallback(
    (scope, updater) => {
      updateScopedForm(scope, (current) => ({ ...current, plans: updater(current.plans || []) }))
    },
    [updateScopedForm]
  )

  const updatePlan = useCallback(
    (scope, planId, updater) => {
      updatePlans(scope, (plans) => plans.map((plan) => (plan.id === planId ? updater(plan) : plan)))
    },
    [updatePlans]
  )

  const updateRow = useCallback(
    (scope, planId, rowId, updater) => {
      updatePlan(scope, planId, (plan) => ({
        ...plan,
        exercises: (plan.exercises || []).map((exercise) => (exercise.id === rowId ? updater(exercise) : exercise)),
      }))
    },
    [updatePlan]
  )

  const getScopeState = useCallback(
    (scope) => (scope === 'create' ? createFormState : editFormState || { globalDefaults: createBulkDefaults(), plans: [] }),
    [createFormState, editFormState]
  )

  const handleAddPlan = (scope) => {
    const name = (scope === 'create' ? createPlanName : editPlanName).trim()
    if (!name) return toast({ title: 'Plan name is required', variant: 'destructive' })
    const nextPlan = createExercisePlanDraft(name, getScopeState(scope).globalDefaults)
    updatePlans(scope, (plans) => [...plans, nextPlan])
    if (scope === 'create') setCreatePlanName('')
    else setEditPlanName('')
  }

  const handleDuplicatePlan = (scope, planId) =>
    updatePlans(scope, (plans) => {
      const target = plans.find((plan) => plan.id === planId)
      if (!target) return plans
      const duplicate = {
        ...target,
        id: createDraftId('plan-copy'),
        persistedId: null,
        name: `Copy of ${target.name}`,
        exercises: (target.exercises || []).map((exercise) => ({
          ...exercise,
          id: createDraftId('row-copy'),
          persistedId: null,
        })),
      }
      return [...plans, duplicate]
    })

  const handleDeletePlan = (scope, planId) => updatePlans(scope, (plans) => plans.filter((plan) => plan.id !== planId))
  const handlePlanDayToggle = (scope, planId, day, checked) => updatePlan(scope, planId, (plan) => ({ ...plan, days: DAY_ORDER.filter((label) => (label === day ? checked : plan.days.includes(label))) }))
  const handleGlobalDefaultChange = (scope, field, value) => updateScopedForm(scope, (current) => ({ ...current, globalDefaults: { ...(current.globalDefaults || createBulkDefaults()), [field]: value } }))
  const handleApplyGlobalDefaults = (scope) => updateScopedForm(scope, (current) => ({ ...current, plans: (current.plans || []).map((plan) => ({ ...plan, defaults: mergeDefaults(plan.defaults, current.globalDefaults), exercises: (plan.exercises || []).map((exercise) => applyDefaultsToRow(exercise, current.globalDefaults)) })) }))
  const handlePlanDefaultsChange = (scope, planId, field, value) => updatePlan(scope, planId, (plan) => ({ ...plan, defaults: { ...(plan.defaults || createBulkDefaults()), [field]: value } }))
  const handleApplyPlanDefaults = (scope, planId) => updatePlan(scope, planId, (plan) => ({ ...plan, exercises: (plan.exercises || []).map((exercise) => applyDefaultsToRow(exercise, plan.defaults)) }))
  const handleAddExerciseRow = (scope, planId) => updatePlan(scope, planId, (plan) => ({ ...plan, exercises: [...(plan.exercises || []), createExerciseRowDraft({ name: '' }, mergeDefaults(getScopeState(scope).globalDefaults, plan.defaults))] }))
  const handleCopyRow = (scope, planId, rowId) => updatePlan(scope, planId, (plan) => ({ ...plan, exercises: (plan.exercises || []).flatMap((exercise) => exercise.id === rowId ? [exercise, { ...exercise, id: createDraftId('row-copy'), persistedId: null }] : [exercise]) }))
  const handleDeleteRow = (scope, planId, rowId) => updatePlan(scope, planId, (plan) => ({ ...plan, exercises: (plan.exercises || []).filter((exercise) => exercise.id !== rowId) }))

  const handleOpenNote = (scope, planId, exercise) => {
    setNoteDialog({ scope, planId, rowId: exercise.id })
    setNoteDraft(exercise.note || '')
  }

  const handleSaveNote = () => {
    if (!noteDialog) return
    updateRow(noteDialog.scope, noteDialog.planId, noteDialog.rowId, (exercise) => ({ ...exercise, note: noteDraft }))
    setNoteDialog(null)
    setNoteDraft('')
  }

  const handleRequestDeleteExercise = useCallback(
    (scope, planId, rowId) => {
      if (scope !== 'edit') {
        handleDeleteRow(scope, planId, rowId)
        return
      }

      const plan = editFormState?.plans?.find((item) => item.id === planId)
      const exercise = plan?.exercises?.find((item) => item.id === rowId)
      if (!plan || !exercise) return

      setDeleteExerciseDialog({
        scope,
        planId,
        rowId,
        planPersistedId: plan.persistedId || null,
        exercisePersistedId: exercise.persistedId || null,
        planName: plan.name || 'Workout',
        exerciseName: exercise.name || 'Exercise',
      })
    },
    [editFormState, handleDeleteRow]
  )

  const handleDeleteExerciseConfirm = async () => {
    if (!deleteExerciseDialog) return

    const { scope, planId, rowId, planPersistedId, exercisePersistedId } = deleteExerciseDialog
    const applyLocalRemoval = () => {
      handleDeleteRow(scope, planId, rowId)
      setEditBaseline((current) => {
        if (!current) return current
        return {
          ...current,
          plans: (current.plans || []).map((plan) =>
            plan.id === planId || (planPersistedId && plan.persistedId === planPersistedId)
              ? { ...plan, exercises: (plan.exercises || []).filter((exercise) => exercise.id !== rowId && exercise.persistedId !== exercisePersistedId) }
              : plan
          ),
        }
      })
      setDeleteExerciseDialog(null)
    }

    if (!planPersistedId || !exercisePersistedId) {
      applyLocalRemoval()
      return
    }

    setDeletingExerciseId(exercisePersistedId)
    const response = await apiService.deleteExercisePlanTemplatePlanExercises(companyUrl, planPersistedId, [exercisePersistedId])
    setDeletingExerciseId(null)

    if (!response.success) {
      toast({ title: 'Unable to delete exercise', description: response.error, variant: 'destructive' })
      return
    }

    toast({ title: 'Exercise deleted successfully' })
    applyLocalRemoval()
  }

  const handleDragEndFactory = (scope) => async (event) => {
    const { active, over } = event
    const clearDragState = () => {
      if (scope === 'create') setCreateDragNode(null)
      else setEditDragNode(null)
    }

    try {
      const activeType = active.data?.current?.type
      const overType = over?.data?.current?.type
      const node = active.data?.current?.node
      const scopedState = getScopeState(scope)

      if (activeType === 'exerciseRow' && overType === 'exerciseRow') {
        const activePlanId = active.data?.current?.planId
        const overPlanId = over.data?.current?.planId
        if (!activePlanId || activePlanId !== overPlanId) return
        updatePlan(scope, activePlanId, (plan) => {
          const rows = plan.exercises || []
          const oldIndex = rows.findIndex((row) => row.id === active.id)
          const newIndex = rows.findIndex((row) => row.id === over.id)
          if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return plan
          return { ...plan, exercises: arrayMove(rows, oldIndex, newIndex) }
        })
        return
      }

      if (activeType !== 'exerciseLibraryItem' || !node) return

      const nodeType = classifyExerciseDropNode(node)
      const shouldImportPlan =
        nodeType === 'plan-template' &&
        node.sourceId &&
        (overType === 'planCanvas' || overType === 'planDrop' || overType === 'exerciseRow' || (!over && scopedState.plans.length === 0))

      if (shouldImportPlan) {
        const response = await apiService.fetchExercisePlanTemplateDetails(companyUrl, node.sourceId)
        if (!response.success) {
          toast({ title: 'Unable to import exercise plan template', description: response.error, variant: 'destructive' })
          return
        }
        const normalized = normalizeExercisePlanTemplateDetail(response.data, { id: node.sourceId, name: node.name })
        const importedPlans = (normalized.plans || []).map(cloneImportedPlan)
        if (!importedPlans.length) return
        updatePlans(scope, (plans) => [...plans, ...importedPlans])
        return
      }

      if (!(overType === 'planDrop' || overType === 'exerciseRow')) return
      const targetPlanId = over.data?.current?.planId
      if (!targetPlanId) return

      const targetPlan = (scopedState.plans || []).find((plan) => plan.id === targetPlanId)
      if (!targetPlan) return

      const existingRows = targetPlan.exercises || []
      const insertIndexRaw = overType === 'exerciseRow' ? existingRows.findIndex((row) => row.id === over.id) : existingRows.length
      const insertIndex = insertIndexRaw < 0 ? existingRows.length : insertIndexRaw
      const defaults = mergeDefaults(scopedState.globalDefaults, targetPlan.defaults)

      const rowsToAdd = flattenExerciseNode(node)
        .filter((leaf) => !leaf.isExercisePlanTemplate)
        .map((leaf) =>
          createExerciseRowDraft(
            {
              templateId: leaf.templateSourceId || leaf.templateId || leaf.exercisetemplate_id || leaf.sourceId || leaf.id || null,
              catId: leaf.catid || leaf.cat_id || '',
              catName: leaf.catname || leaf.cat_name || leaf.category || '',
              name: leaf.name || '',
              sets: hasMeaningfulValue(leaf.sets) ? leaf.sets : defaults.sets,
              reps: hasMeaningfulValue(leaf.reps) ? leaf.reps : defaults.reps,
              rest: hasMeaningfulValue(leaf.rest) ? leaf.rest : defaults.rest,
              speed: hasMeaningfulValue(leaf.speed) ? leaf.speed : defaults.speed,
              mode: leaf.mode ?? '',
              intensity: leaf.intensity ?? '',
              heartrate: leaf.heartrate ?? '',
              duration: leaf.duration ?? '',
              note: leaf.note ?? '',
              description: leaf.description ?? '',
              video_url: leaf.video_url ?? '',
            },
            defaults
          )
        )

      if (!rowsToAdd.length) return

      updatePlan(scope, targetPlanId, (plan) => ({
        ...plan,
        exercises: [
          ...(plan.exercises || []).slice(0, insertIndex),
          ...rowsToAdd,
          ...(plan.exercises || []).slice(insertIndex),
        ],
      }))
    } finally {
      clearDragState()
    }
  }

  const validateForm = (state) => {
    const hasCategory = Boolean(state.categoryId)
    const hasNewCategory = Boolean(state.newCategoryName.trim())
    if (!state.name.trim()) return 'Template name is required'
    if (!hasCategory && !hasNewCategory) return 'Select an exercise plan category or create a new one'
    if (hasCategory && (state.newCategoryName.trim() || state.newCategoryParentId)) return 'Use either an existing category or a new category flow'
    if (!state.plans.length) return 'Add at least one workout plan before saving'
    if (state.plans.some((plan) => !plan.name.trim())) return 'Every workout plan needs a name'
    return ''
  }

  const handleSaveCreate = async () => {
    const validationError = validateForm(createFormState)
    if (validationError) return toast({ title: validationError, variant: 'destructive' })

    setCreateSaving(true)
    const response = await apiService.createExercisePlanTemplate(companyUrl, buildExercisePlanTemplatePayload(createFormState))
    setCreateSaving(false)
    if (!response.success) return toast({ title: 'Unable to create exercise plan template', description: response.error, variant: 'destructive' })

    toast({ title: 'Exercise plan template created successfully' })
    setCreateFormState({
      name: '',
      description: '',
      instructions: '',
      categoryId: '',
      newCategoryName: '',
      newCategoryParentId: '',
      globalDefaults: createBulkDefaults(),
      plans: [],
    })
    setCreatePlanName('')
    await Promise.all([loadCategories(), loadCategoryRows(categoryFilter), loadProgramRows()])
    onTabChange('edit')
  }

  const closeEditWorkspace = useCallback(() => {
    setSelectedProgramId(null)
    setLoadingProgramDetail(false)
    setEditFormState(null)
    setEditBaseline(null)
    setEditPlanName('')
    setEditDragNode(null)
    setEditCatalogCollapsed(false)
  }, [])

  const handleSaveEdit = async () => {
    if (!editFormState?.id) return
    const validationError = validateForm(editFormState)
    if (validationError) return toast({ title: validationError, variant: 'destructive' })

    setEditSaving(true)
    const baseline = editBaseline || {}
    const requiresStructureSync =
      (editFormState.plans || []).some((plan) => !plan.persistedId || (plan.exercises || []).some((exercise) => !exercise.persistedId)) ||
      (baseline.plans || []).some((plan) => plan.persistedId && !(editFormState.plans || []).some((currentPlan) => currentPlan.persistedId === plan.persistedId))

    if (requiresStructureSync) {
      const structureResponse = await apiService.updateExercisePlanTemplate(companyUrl, editFormState.id, buildExercisePlanTemplatePayload(editFormState))
      if (!structureResponse.success) {
        setEditSaving(false)
        return toast({ title: 'Unable to save exercise plan template', description: structureResponse.error, variant: 'destructive' })
      }
    }

    const requestQueue = []

    if (!areEqualByJson(buildTemplateComparable(editFormState), buildTemplateComparable(baseline))) {
      requestQueue.push(
        apiService.updateExercisePlanTemplate(companyUrl, editFormState.id, buildTemplateUpdatePayload(editFormState))
      )
    }

    ;(editFormState.plans || []).forEach((plan) => {
      if (!plan.persistedId) return
      const baselinePlan = (baseline.plans || []).find((item) => item.persistedId === plan.persistedId)
      if (baselinePlan && !areEqualByJson(buildPlanComparable(plan), buildPlanComparable(baselinePlan))) {
        requestQueue.push(
          apiService.updateExercisePlanTemplatePlan(companyUrl, editFormState.id, plan.persistedId, buildPlanUpdatePayload(plan))
        )
      }

      ;(plan.exercises || []).forEach((exercise) => {
        if (!exercise.persistedId) return
        const baselineExercise = baselinePlan?.exercises?.find((item) => item.persistedId === exercise.persistedId)
        if (baselineExercise && !areEqualByJson(buildExerciseComparable(exercise), buildExerciseComparable(baselineExercise))) {
          requestQueue.push(
            apiService.updateExercisePlanTemplateExercise(companyUrl, plan.persistedId, exercise.persistedId, buildExerciseUpdatePayload(exercise))
          )
        }
      })
    })

    const childResults = requestQueue.length ? await Promise.all(requestQueue) : []
    setEditSaving(false)

    const failedChild = childResults.find((result) => !result.success)
    if (failedChild) {
      return toast({
        title: 'Some exercise plan changes failed to save',
        description: failedChild.error,
        variant: 'destructive',
      })
    }

    if (!requestQueue.length && !requiresStructureSync) {
      toast({ title: 'No exercise plan changes to save' })
      return
    }

    toast({ title: 'Exercise plan template updated' })
    await loadProgramRows()
    closeEditWorkspace()
  }

  const handleDeleteSelected = async () => {
    if (!selectedProgramIds.length) return
    const response = await apiService.deleteExercisePlanTemplates(companyUrl, selectedProgramIds)
    if (!response.success) return toast({ title: 'Unable to delete exercise plan templates', description: response.error, variant: 'destructive' })
    toast({ title: 'Exercise plan templates deleted' })
    if (selectedProgramId && selectedProgramIds.includes(selectedProgramId)) closeEditWorkspace()
    await loadProgramRows()
  }

  const handleCategoryParentChange = async (categoryId, value) => {
    setUpdatingCategoryId(categoryId)
    const response = await apiService.updateExercisePlanTemplateCategory(companyUrl, categoryId, { parent_id: value ? Number(value) : null })
    setUpdatingCategoryId(null)
    if (!response.success) return toast({ title: 'Unable to update category', description: response.error, variant: 'destructive' })
    toast({ title: 'Category updated successfully' })
    await Promise.all([loadCategories(), loadCategoryRows(categoryFilter)])
  }

  const handleDeleteCategoryConfirm = async () => {
    if (!deleteCategoryDialog?.id) return
    setDeletingCategoryId(deleteCategoryDialog.id)
    const response = await apiService.deleteExercisePlanTemplateCategory(companyUrl, deleteCategoryDialog.id)
    setDeletingCategoryId(null)
    if (!response.success) {
      toast({ title: 'Unable to delete category', description: response.error, variant: 'destructive' })
      return
    }
    toast({ title: 'Category deleted successfully' })
    setDeleteCategoryDialog(null)
    const nextFilter = `${categoryFilter}` === `${deleteCategoryDialog.id}` ? 'all' : categoryFilter
    if (nextFilter !== categoryFilter) setCategoryFilter(nextFilter)
    await Promise.all([loadCategories(), loadCategoryRows(nextFilter)])
  }

  const renderProgramCatalogCard = (collapsed = false) => {
    if (collapsed) {
      return (
        <Card className="border-slate-200 shadow-sm xl:sticky xl:top-6">
          <CardContent className="flex flex-col items-center gap-4 p-3">
            <button
              type="button"
              onClick={() => setEditCatalogCollapsed(false)}
              className="flex h-11 w-11 items-center justify-center rounded-lg border border-fitness-primary/20 bg-fitness-primary/10 text-fitness-primary transition hover:bg-fitness-primary/15"
              title="Browse exercise plan templates"
            >
              <LayoutGrid className="h-5 w-5" />
            </button>
            <div className="w-full rounded-lg bg-slate-50 px-2 py-3 text-center">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Editing</p>
              <p className="mt-2 break-words text-xs font-semibold text-slate-900">{editFormState?.name || 'Program'}</p>
            </div>
          </CardContent>
        </Card>
      )
    }

    return (
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <CardTitle className="text-lg">Exercise Plan Catalog</CardTitle>
              <CardDescription>Search saved templates, load the full builder on the right, and edit nested workout plans without losing space.</CardDescription>
            </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
              <div className="relative sm:min-w-[240px]">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  value={programSearch}
                  onChange={(event) => {
                    setProgramPagination((current) => ({ ...current, pageIndex: 0 }))
                    setProgramSearch(event.target.value)
                  }}
                  placeholder="Search"
                  className="w-full pl-9"
                />
              </div>
              <Button type="button" variant="destructive" disabled={!selectedProgramIds.length} onClick={handleDeleteSelected}>
                <Trash2 className="mr-2 h-4 w-4" />
                Delete Selected
              </Button>
                {editForm ? <Button type="button" variant="outline" onClick={() => setEditCatalogCollapsed(true)}>Focus Editor</Button> : null}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {loadingProgramRows ? (
            <EmptyState message="Loading exercise plan templates..." />
          ) : programRows.length ? (
            <>
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-4 py-3"><input type="checkbox" checked={programRows.length > 0 && programRows.every((row) => selectedProgramIds.includes(row.id))} onChange={(event) => setSelectedProgramIds(event.target.checked ? programRows.map((row) => row.id) : [])} /></th>
                      <th className="px-4 py-3">Name</th>
                      <th className="px-4 py-3">Category</th>
                      <th className="px-4 py-3">Description</th>
                      <th className="px-4 py-3">Instructions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {programRows.map((row) => (
                      <tr key={row.id} onClick={() => loadProgramDetail(row.id, row)} className={cn('cursor-pointer transition hover:bg-slate-50', selectedProgramId === row.id && 'bg-fitness-primary/5')}>
                        <td className="px-4 py-3" onClick={(event) => event.stopPropagation()}>
                          <input type="checkbox" checked={selectedProgramIds.includes(row.id)} onChange={() => setSelectedProgramIds((current) => (current.includes(row.id) ? current.filter((id) => id !== row.id) : [...current, row.id]))} />
                        </td>
                        <td className="px-4 py-3 font-medium text-slate-900">{row.name}</td>
                        <td className="px-4 py-3 text-slate-600">{row.exerciseplantemplatecategory?.name || 'Uncategorized'}</td>
                        <td className="px-4 py-3 text-slate-600">{row.description || 'No description'}</td>
                        <td className="px-4 py-3 text-slate-600">{row.instructions || 'No instructions'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <PaginationFooter pageIndex={programPagination.pageIndex} pageSize={programPagination.pageSize} totalCount={programTotalCount} onPageChange={(pageIndex) => setProgramPagination((current) => ({ ...current, pageIndex }))} />
            </>
          ) : (
            <EmptyState message="No exercise plan templates matched the current search." />
          )}
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="uploader-page">
      <PageHero title="Exercise Plan Templates" description="Create reusable workout templates, edit saved plan structures, and keep category layout aligned with the rest of the uploader theme." onBack={onBack} />
      <WorkspaceTabs tabs={EXERCISE_PLAN_TEMPLATE_TABS} activeTab={activeTab} onChange={onTabChange} />
      <div className="uploader-surface space-y-6">

      {activeTab === 'create' ? (
        <ExercisePlanCanvas
          mode="create"
          form={createForm}
          newPlanName={createPlanName}
          onNewPlanNameChange={setCreatePlanName}
          onAddPlan={() => handleAddPlan('create')}
          globalDefaults={createFormState.globalDefaults}
          onGlobalDefaultChange={(field, value) => handleGlobalDefaultChange('create', field, value)}
          onApplyGlobalDefaults={() => handleApplyGlobalDefaults('create')}
          onPlanNameChange={(planId, value) => updatePlan('create', planId, (plan) => ({ ...plan, name: value }))}
          onPlanDefaultsChange={(planId, field, value) => handlePlanDefaultsChange('create', planId, field, value)}
          onApplyPlanDefaults={(planId) => handleApplyPlanDefaults('create', planId)}
          onPlanCopy={(planId) => handleDuplicatePlan('create', planId)}
          onPlanDelete={(planId) => handleDeletePlan('create', planId)}
          onPlanDayToggle={(planId, day, checked) => handlePlanDayToggle('create', planId, day, checked)}
                  onAddExerciseRow={(planId) => handleAddExerciseRow('create', planId)}
                  onRowFieldChange={(planId, rowId, field, value) => updateRow('create', planId, rowId, (exercise) => ({ ...exercise, [field]: value }))}
                  onRowCopy={(planId, rowId) => handleCopyRow('create', planId, rowId)}
                  onRowDelete={(planId, rowId) => handleRequestDeleteExercise('create', planId, rowId)}
                  onRequestNote={(planId, exercise) => handleOpenNote('create', planId, exercise)}
          libraryTree={libraryTree}
          libraryExpanded={libraryExpanded}
          onToggleLibraryNode={(nodeId) => setLibraryExpanded((current) => ({ ...current, [nodeId]: !current[nodeId] }))}
          onExpandAllLibrary={(checked) => setLibraryExpanded(buildLibraryExpansionState(libraryTree, checked))}
          libraryFilter={libraryFilter}
          onLibraryFilterChange={setLibraryFilter}
          activeDragNode={createDragNode}
          onDragStart={(event) => event.active.data?.current?.type === 'exerciseLibraryItem' && setCreateDragNode(event.active.data.current.node)}
          onDragCancel={() => setCreateDragNode(null)}
          onDragEnd={handleDragEndFactory('create')}
          onReset={() => {
            setCreateFormState({
              name: '',
              description: '',
              instructions: '',
              categoryId: '',
              newCategoryName: '',
              newCategoryParentId: '',
              globalDefaults: createBulkDefaults(),
              plans: [],
            })
            setCreatePlanName('')
          }}
          onSave={handleSaveCreate}
          saveLabel="Save Exercise Plan"
          saveLoading={createSaving}
        />
      ) : null}

      {activeTab === 'edit' ? (
      <div className={cn('grid gap-6', editForm && editCatalogCollapsed ? 'xl:grid-cols-[88px,minmax(0,1fr)]' : 'grid-cols-1')}>
          {editForm && editCatalogCollapsed ? (
            <div className="space-y-6">{renderProgramCatalogCard(true)}</div>
          ) : (
            <div className="space-y-6">{renderProgramCatalogCard(false)}</div>
          )}

          {editForm && editCatalogCollapsed ? (
            <div className="space-y-6">

              {loadingProgramDetail ? (
                <EmptyState message="Loading exercise plan template detail..." />
              ) : (
                <ExercisePlanCanvas
                  mode="edit"
                  form={editForm}
                  newPlanName={editPlanName}
                  onNewPlanNameChange={setEditPlanName}
                  onAddPlan={() => handleAddPlan('edit')}
                  globalDefaults={editFormState?.globalDefaults || createBulkDefaults()}
                  onGlobalDefaultChange={(field, value) => handleGlobalDefaultChange('edit', field, value)}
                  onApplyGlobalDefaults={() => handleApplyGlobalDefaults('edit')}
                  onPlanNameChange={(planId, value) => updatePlan('edit', planId, (plan) => ({ ...plan, name: value }))}
                  onPlanDefaultsChange={(planId, field, value) => handlePlanDefaultsChange('edit', planId, field, value)}
                  onApplyPlanDefaults={(planId) => handleApplyPlanDefaults('edit', planId)}
                  onPlanCopy={(planId) => handleDuplicatePlan('edit', planId)}
                  onPlanDelete={(planId) => handleDeletePlan('edit', planId)}
                  onPlanDayToggle={(planId, day, checked) => handlePlanDayToggle('edit', planId, day, checked)}
                  onAddExerciseRow={(planId) => handleAddExerciseRow('edit', planId)}
                  onRowFieldChange={(planId, rowId, field, value) => updateRow('edit', planId, rowId, (exercise) => ({ ...exercise, [field]: value }))}
                  onRowCopy={(planId, rowId) => handleCopyRow('edit', planId, rowId)}
                  onRowDelete={(planId, rowId) => handleRequestDeleteExercise('edit', planId, rowId)}
                  onRequestNote={(planId, exercise) => handleOpenNote('edit', planId, exercise)}
                  libraryTree={libraryTree}
                  libraryExpanded={libraryExpanded}
                  onToggleLibraryNode={(nodeId) => setLibraryExpanded((current) => ({ ...current, [nodeId]: !current[nodeId] }))}
                  onExpandAllLibrary={(checked) => setLibraryExpanded(buildLibraryExpansionState(libraryTree, checked))}
                  libraryFilter={libraryFilter}
                  onLibraryFilterChange={setLibraryFilter}
                  activeDragNode={editDragNode}
                  onDragStart={(event) => event.active.data?.current?.type === 'exerciseLibraryItem' && setEditDragNode(event.active.data.current.node)}
                  onDragCancel={() => setEditDragNode(null)}
                  onDragEnd={handleDragEndFactory('edit')}
                  onReset={() => {
                    setEditFormState(editBaseline)
                    closeEditWorkspace()
                  }}
                  onSave={handleSaveEdit}
                  saveLabel="Save Exercise Plan"
                  saveLoading={editSaving}
                />
              )}
            </div>
      ) : null}
        </div>
      ) : null}

      {activeTab === 'categories' ? (
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <CardTitle className="text-lg">Exercise Plan Categories</CardTitle>
                <CardDescription>Keep the category hierarchy clean and review which exercise plan templates live in each category.</CardDescription>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-slate-600">View category layout</span>
                <select className="flex h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-fitness-primary focus:outline-none" value={categoryFilter} onChange={async (event) => { const value = event.target.value; setCategoryFilter(value); await loadCategoryRows(value) }}>
                  <option value="all">All Categories</option>
                  {categoryFilterOptions.map((category) => <option key={`exercise-filter-${category.id}`} value={category.id}>{category.name}</option>)}
                </select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loadingCategoryRows ? <EmptyState message="Loading exercise plan categories..." /> : categoryRows.length ? (
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-5 py-3">Name</th>
                      <th className="px-5 py-3">Parent Category</th>
                      <th className="px-5 py-3">Exercise Templates</th>
                      <th className="px-5 py-3 text-right">Delete</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {categoryRows.map((row) => (
                      <tr key={row.id}>
                        <td className="px-5 py-4 align-top font-medium text-slate-900">{row.name}</td>
                        <td className="px-5 py-4 align-top">
                          <select className="flex h-10 min-w-[220px] rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-fitness-primary focus:outline-none" value={row.parent_id || ''} onChange={(event) => handleCategoryParentChange(row.id, event.target.value)} disabled={updatingCategoryId === row.id}>
                            <option value="">No Parent Category</option>
                            {topLevelCategories.filter((candidate) => candidate.id !== row.id).map((candidate) => <option key={`${row.id}-${candidate.id}`} value={candidate.id}>{candidate.name}</option>)}
                          </select>
                        </td>
                        <td className="px-5 py-4 align-top">
                          {row.exerciseplantemplates?.length ? (
                            <div className="flex max-h-[116px] min-h-[116px] flex-wrap content-start gap-2 overflow-y-auto pr-2">
                              {row.exerciseplantemplates.map((template) => <Badge key={template.id} className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">{template.name}</Badge>)}
                            </div>
                          ) : (
                            <div className="flex min-h-[116px] items-start py-1">
                              <span className="text-slate-500">No exercise plan templates assigned</span>
                            </div>
                          )}
                        </td>
                        <td className="px-5 py-4 text-right align-top">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            disabled={deletingCategoryId === row.id}
                            onClick={() => setDeleteCategoryDialog({ id: row.id, name: row.name })}
                          >
                            <Trash2 className="h-4 w-4 text-slate-500" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <EmptyState message="No exercise plan categories matched the current filter." />}
          </CardContent>
        </Card>
      ) : null}
      </div>

      <Dialog open={!!noteDialog} onOpenChange={(open) => (!open ? setNoteDialog(null) : null)}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader><DialogTitle>Exercise Note</DialogTitle></DialogHeader>
          <Textarea value={noteDraft} onChange={(event) => setNoteDraft(event.target.value)} placeholder="Add a note for this exercise row" className="min-h-[180px]" />
          <DialogFooter className="gap-2 sm:justify-end"><Button type="button" variant="outline" onClick={() => setNoteDialog(null)}>Cancel</Button><Button type="button" onClick={handleSaveNote}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteCategoryDialog} onOpenChange={(open) => (!open ? setDeleteCategoryDialog(null) : null)}>
        <DialogContent className="sm:max-w-[460px]">
          <DialogHeader>
            <DialogTitle>Delete Exercise Plan Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-slate-600">
            <p>
              Delete <span className="font-semibold text-slate-900">{deleteCategoryDialog?.name || 'this category'}</span>?
            </p>
            <p>This action cannot be undone.</p>
          </div>
          <DialogFooter className="gap-2 sm:justify-end">
            <Button type="button" variant="outline" onClick={() => setDeleteCategoryDialog(null)} disabled={Boolean(deletingCategoryId)}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={handleDeleteCategoryConfirm} disabled={Boolean(deletingCategoryId)}>
              {deletingCategoryId ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Delete Category
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteExerciseDialog} onOpenChange={(open) => (!open ? setDeleteExerciseDialog(null) : null)}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Delete Exercise</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-slate-600">
            <p>
              Delete <span className="font-semibold text-slate-900">{deleteExerciseDialog?.exerciseName || 'this exercise'}</span>
              {deleteExerciseDialog?.planName ? (
                <>
                  {' '}from <span className="font-semibold text-slate-900">{deleteExerciseDialog.planName}</span>
                </>
              ) : null}
              ?
            </p>
            <p>This action cannot be undone.</p>
          </div>
          <DialogFooter className="gap-2 sm:justify-end">
            <Button type="button" variant="outline" onClick={() => setDeleteExerciseDialog(null)} disabled={Boolean(deletingExerciseId)}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={handleDeleteExerciseConfirm} disabled={Boolean(deletingExerciseId)}>
              {deletingExerciseId ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Delete Exercise
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default ExercisePlanTemplatesWorkspace
